import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRaceEngineContext } from '../RaceEngine/RaceEngineContext';
import { PHASES } from '../RaceEngine/RaceEngineUtils';
import { API_BASE_URL } from '../../../../constants';

/**
 * interface-design: Masterpiece Layout with glassmorphism and subtle neon
 */
const RacePlannerTimeline = () => {
    const { timeline, globalStress, selectedAptitudes, isLoadingDB, dbError, allRacesDB } = useRaceEngineContext();

    // Mapa de número de mes a nombre corto en inglés
    const MONTH_NAMES = { 1:'JAN',2:'FEB',3:'MAR',4:'APR',5:'MAY',6:'JUN',7:'JUL',8:'AUG',9:'SEP',10:'OCT',11:'NOV',12:'DEC' };

    const fanStats = useMemo(() => {
        const scheduled = timeline.filter(t => t.scheduledRace);
        const getFansEarned = (turn) => {
            const sched = turn.scheduledRace.schedules?.find(s => Number(s.turn) === turn.id);
            return sched?.fans_earned || 0;
        };
        const total = scheduled.reduce((sum, t) => sum + getFansEarned(t), 0);
        const byPhase = {};
        for (const t of scheduled) {
            byPhase[t.phase] = (byPhase[t.phase] || 0) + getFansEarned(t);
        }
        return { total, byPhase, count: scheduled.length };
    }, [timeline]);

    // Agrupar timeline por Fases (Junior, Classic, Senior, URA)
    const groupedTimeline = timeline.reduce((acc, turn) => {
        if (!acc[turn.phase]) acc[turn.phase] = [];
        acc[turn.phase].push(turn);
        return acc;
    }, {});

    // Determinar color de fase
    const getPhaseColor = (phase) => {
        switch (phase) {
            case PHASES.JUNIOR: return 'bg-[#48E5B9]/10 text-[#48E5B9] border-[#48E5B9]/30';
            case PHASES.CLASSIC: return 'bg-[#FAB0C0]/10 text-[#FAB0C0] border-[#FAB0C0]/30';
            case PHASES.SENIOR: return 'bg-yellow-400/10 text-yellow-400 border-yellow-400/30';
            case PHASES.URA: return 'bg-purple-400/10 text-purple-400 border-purple-400/30';
            default: return 'bg-white/10 text-white border-white/20';
        }
    };

    // Estilo de bloque basado en grado de carrera (G1=Dorado, G2=Plata, G3=Verde, resto=Teal)
    const getBlockStyle = (turn) => {
        if (!turn.isPlayable) return 'bg-slate-200 dark:bg-white/[0.02] border-transparent opacity-30 cursor-not-allowed';
        if (turn.scheduledRace) {
            const grade = turn.scheduledRace.grade;
            if (grade === 'G1') return 'bg-gradient-to-br from-yellow-300/90 to-amber-500/90 border-amber-300/60 shadow-[0_0_18px_rgba(251,191,36,0.5)] cursor-pointer';
            if (grade === 'G2') return 'bg-gradient-to-br from-slate-300/90 to-slate-500/90 dark:from-slate-400/80 dark:to-slate-600/80 border-slate-300/60 shadow-[0_0_14px_rgba(148,163,184,0.4)] cursor-pointer';
            if (grade === 'G3') return 'bg-gradient-to-br from-emerald-400/80 to-green-600/80 border-emerald-300/60 shadow-[0_0_14px_rgba(52,211,153,0.4)] cursor-pointer';
            return 'bg-gradient-to-br from-[#48E5B9]/70 to-teal-600/70 border-[#48E5B9]/40 shadow-[0_0_10px_rgba(72,229,185,0.3)] cursor-pointer';
        }
        return 'bg-white/30 dark:bg-[#0B0F19]/40 border-slate-300/50 dark:border-white/10 hover:border-[#48E5B9]/50 hover:bg-white/60 dark:hover:bg-white/10 cursor-pointer transition-colors';
    };

    // Color del badge de grado (texto sobre la imagen del banner)
    const getGradeBadgeClass = (grade) => {
        if (grade === 'G1') return 'bg-amber-400/90 text-amber-900';
        if (grade === 'G2') return 'bg-slate-300/90 text-slate-800';
        if (grade === 'G3') return 'bg-emerald-400/90 text-emerald-900';
        return 'bg-teal-400/90 text-teal-900';
    };

    // URL del banner de la carrera (mismo patrón que RaceList.js:127)
    const getRaceBannerUrl = (scheduledRace) =>
        `${API_BASE_URL}/api/thumb?img=/images/banner/races/banner_race_${scheduledRace.banner_id}.png&width=128`;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="w-full relative mt-12 mb-24"
        >
            {/* Indicador de estado del sistema */}
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-8 pb-6 border-b border-slate-200 dark:border-white/10">
                <div>
                    <h2 className="text-3xl font-black text-slate-800 dark:text-white uppercase tracking-tighter flex items-center gap-3">
                        Timeline Optimizer
                        {globalStress?.tag === 'STRESS' && (
                            <span className="text-[10px] bg-amber-500/20 text-amber-500 px-2 py-1 rounded-sm border border-amber-500/30 tracking-widest">
                                3 consecutivas detectadas
                            </span>
                        )}
                        {globalStress?.tag === 'PENALTY' && (
                            <span className="text-[10px] bg-red-500/20 text-red-500 px-2 py-1 rounded-sm border border-red-500/30 tracking-widest">
                                Racha extrema — revisar ruta
                            </span>
                        )}
                    </h2>
                    <p className="text-slate-500 dark:text-white/40 text-xs tracking-widest uppercase mt-2">
                        72 Turn Matrix • Auto-Routing Engine
                    </p>
                </div>

                <div className="flex flex-col items-end gap-2 mt-6 md:mt-0">
                    {/* Status line */}
                    <p className={`text-[11px] font-semibold tracking-wide ${
                        isLoadingDB ? 'text-slate-400 dark:text-white/30 animate-pulse' :
                        dbError    ? 'text-red-500' :
                        !selectedAptitudes ? 'text-yellow-500 dark:text-yellow-400' :
                        'text-emerald-500 dark:text-[#48E5B9]'
                    }`}>
                        {isLoadingDB && 'Cargando catálogo de carreras...'}
                        {!isLoadingDB && dbError && `Error al cargar carreras: ${dbError}`}
                        {!isLoadingDB && !dbError && !selectedAptitudes && 'Selecciona un personaje primero'}
                        {!isLoadingDB && !dbError && selectedAptitudes && `${allRacesDB.length} carreras disponibles • Ruta generada automáticamente`}
                    </p>

                    {/* Fan count estimate */}
                    {fanStats.count > 0 && (
                        <div className="text-right">
                            <p className="text-[11px] tracking-wide text-slate-500 dark:text-white/30">
                                <span className="font-black text-slate-700 dark:text-white/70">
                                    ~{fanStats.total.toLocaleString()}
                                </span>
                                {' '}fans aprox. &bull; {fanStats.count} carreras
                            </p>
                            <div className="flex gap-3 justify-end mt-0.5">
                                {[PHASES.JUNIOR, PHASES.CLASSIC, PHASES.SENIOR].map(phase =>
                                    fanStats.byPhase[phase] ? (
                                        <span key={phase} className="text-[10px] text-slate-400 dark:text-white/20">
                                            {phase.slice(0, 1)}: {fanStats.byPhase[phase].toLocaleString()}
                                        </span>
                                    ) : null
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Matrix de Turnos - Contenedor Principal (Organiza los 3 bloques de años lado a lado estirándose al 100% del contenedor) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
                {[PHASES.JUNIOR, PHASES.CLASSIC, PHASES.SENIOR].map((phaseKey, index) => {
                    const turns = groupedTimeline[phaseKey];
                    if (!turns || turns.length === 0) return null;

                    return (
                        <motion.div 
                            key={phaseKey}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + (index * 0.1), duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="bg-white/40 dark:bg-[#0B0F19]/40 backdrop-blur-xl border border-slate-200/50 dark:border-white/5 rounded-3xl p-6 shadow-xl dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] flex flex-col"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <span className={`text-[10px] md:text-xs font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm border ${getPhaseColor(phaseKey)}`}>
                                    {phaseKey} Year
                                </span>
                            </div>

                            {/* Matriz 4x6 Estricta para los Años (24 turnos). Se estira completamente llenando el ancho asignado */}
                            <div className="grid gap-3 w-full grid-cols-4 grid-rows-6 flex-grow">
                                <AnimatePresence>
                                    {turns.map((turn, tIdx) => (
                                        <motion.div 
                                            key={turn.id}
                                            layout
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ 
                                                delay: turn.scheduledRace ? tIdx * 0.02 + 0.1 : 0, 
                                                duration: 0.3, 
                                                type: "spring", 
                                                stiffness: 300 
                                            }}
                                            // Se elimina el aspecto cuadrado; las celdas se adaptan como rectángulos HUD
                                            className={`relative w-full h-full min-h-[64px] rounded-lg border flex flex-col items-center justify-center transition-all ${getBlockStyle(turn)}`}
                                            title={`Turno ${turn.id} (Mes ${turn.month} - Q${turn.half})`}
                                        >
                                            {/* Contenido de la celda */}
                                            {turn.isPlayable && (
                                                turn.scheduledRace ? (
                                                    // Carrera agendada: Banner como fondo + badge de grado
                                                    <>
                                                        <img
                                                            src={getRaceBannerUrl(turn.scheduledRace)}
                                                            alt={turn.scheduledRace.race_name}
                                                            className="absolute inset-0 w-full h-full object-cover rounded-lg opacity-70"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                        {/* Overlay con info */}
                                                        <div className="absolute inset-0 flex flex-col items-center justify-end p-1.5 bg-gradient-to-t from-black/70 to-transparent rounded-lg">
                                                            <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-px rounded-sm mb-0.5 ${getGradeBadgeClass(turn.scheduledRace.grade)}`}>
                                                                {turn.scheduledRace.grade || '—'}
                                                            </span>
                                                            <span className="text-[7px] md:text-[8px] text-white/90 text-center leading-tight font-medium line-clamp-2 px-0.5">
                                                                {turn.scheduledRace.race_name || turn.scheduledRace.name || ''}
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    // Celda vacía: Muestra mes y quincena
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center p-1 pointer-events-none">
                                                        <span className="text-[10px] md:text-sm font-black text-slate-500 dark:text-white/40 leading-none">
                                                            {MONTH_NAMES[turn.month]}
                                                        </span>
                                                        <span className="text-[8px] md:text-[9px] text-slate-400 dark:text-white/20 uppercase tracking-widest mt-1">
                                                            {turn.half === 1 ? 'Early' : 'Late'}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
    );
};

export default RacePlannerTimeline;
